import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Form, ListGroup } from 'react-bootstrap';
import { createPortal } from 'react-dom';

const SUGGESTION_MIN_CHARS = 3;
const MAX_SUGGESTIONS = 8;

const CompanyAutocomplete = ({
  value,
  options,
  onChange,
  size = 'sm',
  placeholder = '—',
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  size?: 'sm' | 'lg';
  placeholder?: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const suggestions =
    value.length >= SUGGESTION_MIN_CHARS
      ? options
          .filter(
            (o) =>
              o.toLowerCase().includes(value.toLowerCase()) &&
              o.toLowerCase() !== value.toLowerCase()
          )
          .slice(0, MAX_SUGGESTIONS)
      : [];

  const showDropdown = open && suggestions.length > 0;

  useLayoutEffect(() => {
    if (showDropdown && inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 2, left: r.left, width: r.width });
    }
  }, [showDropdown, value]);

  useEffect(() => {
    if (!showDropdown) return;
    const reposition = () => {
      if (inputRef.current) {
        const r = inputRef.current.getBoundingClientRect();
        setCoords({ top: r.bottom + 2, left: r.left, width: r.width });
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [showDropdown]);

  return (
    <>
      <Form.Control
        ref={inputRef as any}
        size={size}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {showDropdown &&
        coords &&
        createPortal(
          <ListGroup
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: coords.width,
              zIndex: 1050,
              maxHeight: 200,
              overflowY: 'auto',
              fontSize: '0.85rem',
            }}
          >
            {suggestions.map((s) => (
              <ListGroup.Item
                key={s}
                action
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(s);
                  setOpen(false);
                }}
                style={{ padding: '0.3rem 0.6rem' }}
              >
                {s}
              </ListGroup.Item>
            ))}
          </ListGroup>,
          document.body
        )}
    </>
  );
};

export default CompanyAutocomplete;
